import { Fragment, isValidElement, ReactNode } from 'react';

const FAILURE_PATTERNS = ['Failure of:', 'Compressor Failure:'];

interface StyledTextProps {
  text: string;
  className: string;
}

const StyledText = ({ text, className }: StyledTextProps) => (
  <span className={className}>{text}</span>
);

const renderTextWithPattern = (text: string, pattern: string, className: string): ReactNode => {
  const parts = text.split(pattern);
  if (parts.length <= 1) return text;
  
  return (
    <>
      {parts[0]}{pattern}
      <StyledText text={parts[1]} className={className} />
    </>
  );
};

const renderAndParts = (parts: string[], andText: string): ReactNode => (
  <>
    {parts.map((part, index) => (
      <Fragment key={index}>
        {part}
        {index < parts.length - 1 && (
          <StyledText text={andText} className="logical-operator" />
        )}
      </Fragment>
    ))}
  </>
);

const processFailurePattern = (text: string, pattern: string, andText: string): ReactNode | null => {
  if (!text.includes(pattern)) return null;
  
  const parts = text.split(pattern);
  if (parts.length <= 1) return null;
  
  const andParts = text.split(andText);
  if (andParts.length > 1) {
    return (
      <>
        {andParts[0].includes(pattern) ? (
          <>
            {andParts[0].split(pattern)[0]}{pattern}
            <StyledText text={andParts[0].split(pattern)[1]} className="compressor-names" />
          </>
        ) : andParts[0]}
        <StyledText text={andText} className="logical-operator" />
        {andParts[1].includes(pattern) ? (
          <>
            {andParts[1].split(pattern)[0]}{pattern}
            <StyledText text={andParts[1].split(pattern)[1]} className="compressor-names" />
          </>
        ) : andParts[1]}
      </>
    );
  }
  
  return (
    <>
      {parts[0]}{pattern}
      <StyledText text={parts[1]} className="compressor-names" />
    </>
  );
};

export const renderDescriptionWithStyledAnd = (description: string, andText: string): ReactNode => {
  const andParts = description.split(andText);
  const result: ReactNode = andParts.length > 1 
    ? renderAndParts(andParts, andText) 
    : description;

  for (const pattern of FAILURE_PATTERNS) {
    if (typeof result === 'string' && result.includes(pattern)) {
      return renderTextWithPattern(result, pattern, 'compressor-names');
    }
    
    if (isValidElement(result)) {
      const processedResult = processFailurePattern(description, pattern, andText);
      if (processedResult) return processedResult;
    }
  }

  return result;
};
